import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  trustedContainer: {
    width: '100%',
    backgroundColor: theme.palette.shades['clr-white-900'],
    padding: theme.spacing(20, 37.5),
    overflow: 'hidden',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  contentWrapper: {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  heading: {
    ...theme.typography['h2-bold'],
    color: '#2E3558',
    textAlign: 'center',
    marginBottom: theme.spacing(12),
    wordWrap: 'break-word',
    width: '100%',
    [theme.breakpoints.down('lg')]: {
      ...theme.typography['h3-bold'],
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h4-bold'],
      marginBottom: theme.spacing(8),
    },
  },
  logosContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(8),
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(6),
    },
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2.5),
      justifyContent: 'space-around',
    },
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '80px',
    minWidth: '100px',
    maxWidth: '160px',
    transition: 'all 0.3s ease',
    flexShrink: 0,
    [theme.breakpoints.down('lg')]: {
      height: '70px',
      minWidth: '90px',
      maxWidth: '140px',
    },
    [theme.breakpoints.down('md')]: {
      height: '60px',
      minWidth: '70px',
      maxWidth: '110px',
    },
    [theme.breakpoints.down('sm')]: {
      height: '45px',
      minWidth: '60px',
      maxWidth: '90px',
    },
  },
  logoImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}));

// Component to handle image loading with PNG/JPEG fallback
const InstitutionLogo = ({ name, alt, classes }) => {
  const [imageSrc, setImageSrc] = useState(
    `${process.env.REACT_APP_ASSETS_URL}/images/${name}.png`
  );

  const handleError = () => {
    // Try JPEG if PNG fails
    if (imageSrc.endsWith('.png')) {
      setImageSrc(`${process.env.REACT_APP_ASSETS_URL}/images/${name}.jpeg`);
    } else if (imageSrc.endsWith('.jpeg')) {
      // Try JPG if JPEG fails
      setImageSrc(`${process.env.REACT_APP_ASSETS_URL}/images/${name}.jpg`);
    }
  };

  return (
    <Box className={classes.logoWrapper}>
      <img
        src={imageSrc}
        alt={alt}
        className={classes.logoImage}
        onError={handleError}
      />
    </Box>
  );
};

const TrustedInstitutions = () => {
  const classes = useStyles();

  // All institution logos in one array
  const institutions = [
    { name: 'charles_university', alt: 'Charles University' },
    { name: 'chicago_booth', alt: 'Chicago Booth' },
    { name: 'essca', alt: 'ESSCA' },
    { name: 'harvard', alt: 'Harvard University' },
    { name: 'hec', alt: 'HEC Paris' },
    { name: 'iima', alt: 'IIMA' },
    { name: 'iimb', alt: 'IIMB' },
    { name: 'iimc', alt: 'IIMC' },
    { name: 'iimKozhi', alt: 'IIM Kozhikode' },
    { name: 'iiml', alt: 'IIM Lucknow' },
    { name: 'iimmumbai', alt: 'IIM Mumbai' },
    { name: 'iimu', alt: 'IIM Udaipur' },
    { name: 'iit_bhu', alt: 'IIT BHU' },
    { name: 'iitk', alt: 'IIT Kanpur' },
    { name: 'isb_new', alt: 'ISB' },
    { name: 'src', alt: 'SRC' },
    { name: 'queen_marry', alt: 'Queen Mary University of London' },
    { name: 'xlri_new', alt: 'XLRI' },
    { name: 'yale', alt: 'Yale School of Management' },
  ];

  return (
    <Box className={classes.trustedContainer}>
      <Box className={classes.contentWrapper}>
        {/* Heading */}
        <Typography className={classes.heading}>
          Trusted by Students & Professionals from Top Institutions
        </Typography>

        {/* Institution Logos */}
        <Box className={classes.logosContainer}>
          {institutions.map((institution, index) => (
            <InstitutionLogo
              key={index}
              name={institution.name}
              alt={institution.alt}
              classes={classes}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TrustedInstitutions;
